import { createForm, ErrorsInline, FormValidateParams, SubmitParams } from 'src/infastruct/effector-react-form'
import { combine, createEffect, createEvent, createStore, sample } from 'effector/compat'
import {
    IPrescriptionEnterRequest,
    prescriptionEnterApi,
    prescriptionGetByPrescriptionGuidsApi,
    PrescriptionNumbers
} from '@entities/prescription/api'
import { parseCookies, setCookie } from 'nookies'
import { IPrescription, IPrescriptionFull } from './contract'
import moment from 'moment'
import { IPrescriptionCatalogGoods } from 'src/pages/prescription/contract'
import { IShipment } from 'src/shared/contract/shipment'
import { prescriptionGoodsApi } from 'src/pages/prescription/api'
import { subscribeLoggerEffect } from 'src/infastruct/effector-utils/subscribe-sentry'
import { pushFx } from 'src/infastruct/effector-utils/attach-router'
import { $currentPath } from '@entities/route/model'
import { $shipment } from '@entities/shipment/model'
import { ErrorResponse } from 'src/shared/lib/response-worker/model'
import { addErrorMessage } from 'src/entities/error-message/model'
import { isShowMessage, ShowError } from 'src/shared/lib/message-model/model'
import { subscribeShowErrorEffect } from 'src/shared/model/effect-show-error'
import { $basket } from '@entities/basket/model'
import { IGoodBasket } from '@entities/basket/contract'
import { not } from 'patronum'
import { persist, StorageAdapter } from 'src/shared/lib/effector-storage'

export const $selectedPrescription = createStore<IPrescription[]>({} as IPrescription[])

export const setIsNotRedirectOnCatalog = createEvent<boolean>()
export const resetIsNotRedirectOnCatalog = createEvent()
export const $isNotRedirectOnCatalog = createStore<boolean>(false)
    .on(setIsNotRedirectOnCatalog, (_, payload) => payload)
    .reset(resetIsNotRedirectOnCatalog)

export const $isBasketModalErecipe = createStore(false)
export const toggleBasketModalErecipe = createEvent<boolean>()

sample({
    clock: toggleBasketModalErecipe,
    target: $isNotRedirectOnCatalog
})

const prescriptionCatalogPush = createEvent<string[]>()
export const pickupPrescription = createEvent()
export const onSubmittedErecipe = createEvent()
export const deletePrescriptions = createEvent<string[]>()
export const $prescriptionCatalog = createStore<IPrescriptionCatalogGoods[]>([])

export const $prescriptionCatalogFlat = combine($prescriptionCatalog, (catalog) => {
    return catalog.flatMap((x) => x.goods)
})

interface IPrescriptionLimits {
    maxPartsSum: number
}

export const $prescriptionLimits = createStore<IPrescriptionLimits>({ maxPartsSum: 0 })

// noinspection DuplicatedCode
export const prescriptionGoodsFx = createEffect<
    {
        prescriptionIds: string[]
        shipment: IShipment
    },
    IPrescriptionCatalogGoods[],
    Error
>(async ({ prescriptionIds, shipment }): Promise<IPrescriptionCatalogGoods[]> => {
    if (!prescriptionIds || prescriptionIds.length === 0) return [] as IPrescriptionCatalogGoods[]
    const response = await prescriptionGoodsApi(prescriptionIds, shipment)
    const responseError = response as ErrorResponse
    if (responseError.IsError) {
        throw { showMessage: responseError.Data } as ShowError
    }
    return response as IPrescriptionCatalogGoods[]
})

export const initBasketPrescriptionFx = createEffect<
    {
        prescriptionIds: string[]
        basketItems: IGoodBasket[]
        shipment: IShipment
    },
    IPrescriptionCatalogGoods[],
    Error
>(async ({ prescriptionIds, shipment, basketItems }) => {
    const res = await prescriptionGoodsFx({
        prescriptionIds,
        shipment
    })
    const erecipeGoodIds = res.flatMap((x) => x.goods).map((x) => x.goodsInfo.webData.goodId)
    const isNotFoundGoodInBasket = !basketItems.some((x) => erecipeGoodIds.some((eg) => eg === x.webData.goodId))
    if (isNotFoundGoodInBasket) addErrorMessage('По данному рецепту товары не найдены')
    return res
})

subscribeLoggerEffect(prescriptionGoodsFx)
subscribeShowErrorEffect(prescriptionGoodsFx)

export const prescriptionInitFx = createEffect<
    {
        prescriptionIds: string[]
    },
    IPrescription[],
    Error
>(async ({ prescriptionIds }) => {
    if (!prescriptionIds || prescriptionIds.length === 0) return [] as IPrescription[]
    return await prescriptionGetByPrescriptionGuidsApi(prescriptionIds)
})

subscribeLoggerEffect(prescriptionInitFx)

sample({
    clock: prescriptionGoodsFx.doneData,
    //filter: (data) => !!data && data.length > 0,
    fn: (data) => data,
    target: $prescriptionCatalog
})
sample({
    clock: initBasketPrescriptionFx.doneData,
    //filter: (data) => !!data && data.length > 0,
    fn: (data) => data,
    target: $prescriptionCatalog
})

const adapter: StorageAdapter = <TP>(key: string) => {
    return {
        get(): TP {
            const cookies = parseCookies()
            const res = cookies[key]
            return res ? JSON.parse(res) : null
        },
        set(value: TP) {
            setCookie(null, key, JSON.stringify(value), {
                maxAge: 2 * 60 * 60,
                path: '/'
            })
        }
    }
}
persist({
    key: 'zdr_prescription',
    store: $selectedPrescription,
    adapter,
    pickup: pickupPrescription
})

const prescriptionEnterSubmit = createEvent<SubmitParams<IPrescriptionEnterRequest>>()

interface mapError {
    [key: string]: string
}

const isError = (err: unknown): err is Error => err instanceof Error
const isErrorsInline = (target: unknown): target is Record<string, string | null | undefined> => !isError(target)
const responseError = (values?: {
    invalidParameters?: {
        name: string
        text: string
    }[]
}) => {
    const errors: mapError = {}
    if (!values) return
    if (values?.invalidParameters && values?.invalidParameters.length > 0) {
        values.invalidParameters.forEach((x) => {
            errors[x.name] = x.text
        })
    }
    return errors
}

async function enterPrescriptions(
    prescriptionEnterRequest: IPrescriptionEnterRequest,
    selectedPrescriptions?: IPrescription[]
) {
    const request = { ...prescriptionEnterRequest }
    request.birthday = request.birthday && moment(request.birthday, 'DD.MM.YYYY').format()
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    request.prescriptionNumbers = Object.values(request.prescriptionNumbers).filter((x) => x.length > 0)
    request.prevPrescriptionGuids = selectedPrescriptions?.map((x) => x.prescriptionGuid)

    const response = await prescriptionEnterApi(request)
    if ((response as ErrorResponse).IsError && (response as ErrorResponse).TypeDto === 5) {
        throw { showMessage: (response as ErrorResponse).Data } as ShowError
    }
    const serverSideError = responseError(
        response as {
            invalidParameters: {
                name: string
                text: string
            }[]
        }
    )
    if (serverSideError && Object.keys(serverSideError).length > 0) {
        throw serverSideError
    }

    return response as IPrescriptionFull
}

const prescriptionEnter = createEvent<IPrescriptionEnterRequest>()
export const prescriptionEnterSubmitFx = createEffect<
    {
        prescriptionEnterRequest: IPrescriptionEnterRequest
        selectedPrescriptions?: IPrescription[]
    },
    IPrescriptionFull,
    Error
>(async ({ prescriptionEnterRequest, selectedPrescriptions }) => {
    return await enterPrescriptions(prescriptionEnterRequest, selectedPrescriptions)
})

sample({
    clock: prescriptionEnter,
    source: $selectedPrescription,
    fn: (selectedPrescriptions, prescriptionEnterRequest) => ({
        prescriptionEnterRequest,
        selectedPrescriptions
    }),
    target: prescriptionEnterSubmitFx
})

const formValidate = ({
    values
}: FormValidateParams<
    IPrescriptionEnterRequest,
    {
        mode: 'change'
    }
>) => {
    const errors: ErrorsInline = {}

    const { birthday, prescriptionNumbers, fullName } = values
    if (!birthday) {
        errors['birthday'] = 'Введена некорректная Дата рождения'
    }
    if (!prescriptionNumbers['0']) {
        errors['prescriptionNumber'] = 'Введен некорректный номер электронного рецепта'
    }
    if (!fullName) {
        errors['fullName'] = 'Введено некорректное ФИО'
    }
    if (birthday && !moment(birthday, 'DD.MM.YYYY').isValid()) {
        errors['birthday'] = 'Введена некорректная Дата рождения'
    }
    if (birthday) {
        birthday.split('.').forEach((x, index) => {
            if (index < 1 && x.length != 2) {
                errors['birthday'] = 'Введена некорректная Дата рождения'
                return
            }
            if (index === 2 && x.length != 4) {
                errors['birthday'] = 'Введена некорректная Дата рождения'
                return
            }
        })
    }
    if (fullName && fullName.split(' ').length < 3) {
        errors['fullName'] = 'Введено некорректное ФИО'
    }

    return errors
}

sample({
    clock: toggleBasketModalErecipe,
    source: $isBasketModalErecipe,
    fn: (status) => !status,
    target: $isBasketModalErecipe
})

sample({
    clock: $currentPath,
    fn: () => false,
    target: $isBasketModalErecipe
})

export const prescriptionEnterForm = createForm<IPrescriptionEnterRequest>(
    {
        onSubmit: prescriptionEnterSubmit,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        validate: formValidate,
        initialValues: {
            fullName: '',
            birthday: '',
            prescriptionNumbers: {
                '0': '',
                '1': ''
            }
        }
    },
    {
        sid: 'prescriptionEnterForm',
        name: 'prescriptionEnterForm_name'
    }
)
sample({
    source: $isBasketModalErecipe,
    target: prescriptionEnterForm.reset
})

sample({
    clock: prescriptionEnterForm.onChangeFieldBrowser,
    source: prescriptionEnterForm.$values,
    filter: (_, { name }: { name: string }) => {
        return name.includes('prescriptionNumbers')
    },
    fn: (state, { name }) => {
        const index = parseInt(name.split('.')[1])
        const newPrescriptionNumbers: PrescriptionNumbers = {}
        const keys = Object.keys(state.prescriptionNumbers)
        let i = 0
        while (i < index || i < keys.length) {
            if (!state.prescriptionNumbers[i.toString()]) break
            newPrescriptionNumbers[i.toString()] = state.prescriptionNumbers[i.toString()]
            i++
        }
        return {
            ...state,
            prescriptionNumbers: { ...newPrescriptionNumbers }
        }
    },
    target: prescriptionEnterForm.$values
})
sample({
    clock: prescriptionEnterForm.onSubmit,
    source: $selectedPrescription,
    fn: (selectedPrescriptions, form) => ({
        prescriptionEnterRequest: form.values,
        selectedPrescriptions
    }),
    target: prescriptionEnterSubmitFx
})
sample({
    clock: prescriptionEnterSubmitFx.failData.filter({ fn: isErrorsInline }),
    source: prescriptionEnterForm.$values,
    fn: (_, clock) => {
        const err: ErrorsInline = {}
        Object.keys(clock).forEach((key) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            err[key] = clock[key]
        })
        return err
    },
    target: prescriptionEnterForm.$outerErrorsInline
})
sample({
    clock: prescriptionEnterSubmitFx.doneData,
    source: $isNotRedirectOnCatalog,
    filter: not($isNotRedirectOnCatalog),
    fn: (_, { newPrescriptions }) => newPrescriptions.map((x) => x.prescriptionGuid),
    target: prescriptionCatalogPush
})

sample({
    clock: prescriptionEnterSubmitFx.failData.filter({ fn: isShowMessage }),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    fn: (error: ShowError) => error.showMessage,
    target: addErrorMessage
})

sample({
    clock: prescriptionCatalogPush,
    fn: (numbers) => `/prescription/catalog?prescriptions=${numbers.join(',')}`,
    target: pushFx
})

sample({
    clock: prescriptionInitFx.doneData,
    target: $selectedPrescription
})
sample({
    clock: prescriptionEnterSubmitFx.doneData,
    fn: ({ allPrescriptions }) => allPrescriptions,
    target: $selectedPrescription
})

sample({
    clock: prescriptionEnterSubmitFx.doneData,
    source: {
        path: $currentPath,
        basketItems: $basket,
        shipment: $shipment
    },
    filter: $isNotRedirectOnCatalog,
    fn: ({ shipment, basketItems }, { allPrescriptions }) => ({
        prescriptionIds: allPrescriptions.map((x) => x.prescriptionGuid),
        basketItems,
        shipment
    }),
    target: initBasketPrescriptionFx
})
sample({
    clock: prescriptionEnterSubmitFx.doneData,
    source: $currentPath,
    filter: $isNotRedirectOnCatalog,
    target: onSubmittedErecipe
})

sample({
    clock: deletePrescriptions,
    source: $selectedPrescription,
    filter: (prescriptions, deletePrescriptionGuids) =>
        prescriptions.length > 0 && deletePrescriptionGuids && deletePrescriptionGuids.length > 0,
    fn: (prescriptions, deletePrescriptionGuids) =>
        prescriptions.filter((x) => !deletePrescriptionGuids.some((d) => d === x.prescriptionGuid)),
    target: $selectedPrescription
})
sample({
    clock: deletePrescriptions,
    source: $prescriptionCatalog,
    filter: (prescriptions, deletePrescriptionGuids) =>
        prescriptions.length > 0 && deletePrescriptionGuids && deletePrescriptionGuids.length > 0,
    fn: (prescriptions, deletePrescriptionGuids) =>
        prescriptions.filter((x) => !deletePrescriptionGuids.some((d) => d === x.prescriptionGuid)),
    target: $prescriptionCatalog
})
sample({
    clock: onSubmittedErecipe,
    fn: () => false,
    target: [$isBasketModalErecipe, $isNotRedirectOnCatalog]
})

sample({
    clock: prescriptionEnterSubmitFx.done,
    fn: () => false,
    target: [$isBasketModalErecipe, $isNotRedirectOnCatalog]
})
